import React, { useContext, useEffect, useState } from "react";
import HelpSection from "../../../common/components/HelpSection/HelpSection";
import { QuoteGroupContext } from "../../providers/RootContextProvider";
import { IQuoteCardResult } from "../../interfaces/results/IQuoteCardResult";
import AlertOldVersion from "../../../common/components/Alert/Static/AlertOldVersion";
import styles from "./QuoteResultsContainer.module.scss";
import cx from "classnames";
import QuoteTableWrapper from "../../components/Quotes/QuoteTable/QuoteTableWrapper";
import { setContactDetailsAPI } from "../../api/api";
import QuoteTableSkeletonLoader from "../../components/Quotes/Components/QuoteTableSkeletonLoader/QuoteTableSkeletonLoader";
import useIsMounted from "../../../common/hooks/useIsMounted";
import { IContactDetails } from "../../../common/interfaces/IContactDetails";
import ContactDetailsModal from "../../components/Modals/ContactDetailsModal/ContactDetailsModal";
import ApplyModal from "../../components/Modals/ApplyModal/ApplyModal";
import SortBy from "../../enums/SortBy";
import QuoteError from "../QuoteError";
import useDataLayer from "../../hooks/useDataLayer";
import { getProductTypeAsString } from "../../../common/shared/enumHelpers";

interface IQuoteResultsContainerBaseProps {
    cards: IQuoteCardResult[];
    sortBy: SortBy;
    caseId: number;
    groupId: string;
    isLoading?: boolean;
    isDisabled?: boolean;
}

const QuoteResultsContainer: React.FC<IQuoteResultsContainerBaseProps> = (props) => {
    const { cards, sortBy, caseId, groupId, isLoading, isDisabled } = props;

    const [cardData, setCardData] = useState<IQuoteCardResult>(null);

    const isMounted = useIsMounted().current;

    const { selectors, actions } = useContext(QuoteGroupContext);

    const { sendPageView } = useDataLayer();

    // Global State
    const clients = selectors.getClients();

    const hasSelectedProduct = false;

    const isLatestVersion = cards.every((card) =>
        card.result.settings.every((setting) => setting.isLatestVersion)
    );

    const handleSubmitContactDetails = (
        newContactDetails: IContactDetails
    ): Promise<boolean> => {
        actions.setHasContactDetails(true);
        actions.setContactDetails(newContactDetails);

        cards[0].result.settings.forEach((setting) => {
            sendPageView(`/compare/${setting.productType}`);
            sendPageView(`/compare/view-results/${setting.productType}`);
        });


        return setContactDetailsAPI(groupId, newContactDetails, caseId);
    };

    const handleApplyNow = async (productId: number): Promise<void> => {
        setCardData(
            cards.find((s) => s.result.benefits.some((b) => b.productId === productId))
        );
    };

    const getURL = (): string => {
        const productList = cardData?.result.settings
            .map(
                (s) =>
                    `${s.caseId}:${cardData?.productInfo[s.productType]?.productId || 0}`
            )
            .join(";");

        return `/compare/apply/form?groupId=${selectors.getGroupId()}&providerId=${
            cardData?.result.providerId
        }&caseProduct=${productList}`;
    };

    useEffect(() => {
        abTesting.convertAll(9, "", getProductTypeAsString, 0);
    }, []);

    const isValid = cards && cards.length > 0;

    if (!isLoading && !isValid) {
        return <QuoteError />;
    }

    return (
        <React.Fragment>
            {!selectors.isFromCrm() && (
                <ContactDetailsModal
                    isOpen={
                        !selectors.hasContactDetails() &&
                        !selectors.isAdmin() &&
                        isMounted
                    }
                    contactDetails={selectors.getContactDetails()}
                    onSubmit={handleSubmitContactDetails}
                />
            )}

            <ApplyModal
                isOpen={!!cardData}
                onClose={() => {
                    setCardData(null);
                }}
                providerId={cardData?.result.providerId}
                clients={clients}
                settings={cardData?.result.settings}
                benefits={cardData?.result.benefits}
                url={getURL()}
                handleClickStart={() => {
                    setCardData(null);
                }}
            />
            {!isLoading && (
                <AlertOldVersion isLatestVersion={isLatestVersion} className="gap-l" />
            )}
            <div className={cx([styles["quote-container"], "gap-l"])}>
                {isLoading ? (
                    <QuoteTableSkeletonLoader className="gap-pre-xl" />
                ) : (
                    <React.Fragment>
                        <div className="gap-l">
                            <QuoteTableWrapper
                                cards={cards}
                                sortBy={sortBy}
                                clients={clients}
                                handlePaymentFrequencyChange={async (freq) =>
                                    actions.setPaymentFrequency(freq)
                                }
                                handleClickCallToAction={handleApplyNow}
                                isAdmin={selectors.isAdmin()}
                                isDisabled={isDisabled}
                            />
                        </div>
                    </React.Fragment>
                )}
            </div>
            {!isLoading && (
                <HelpSection
                    groupId={groupId}
                    contactDetails={selectors.getContactDetails()}
                    isDisabled={hasSelectedProduct}
                    caseId={caseId}
                />
            )}
        </React.Fragment>
    );
};

export default React.memo(QuoteResultsContainer);
